import {CSSProperties, forwardRef, HTMLInputTypeAttribute, InputHTMLAttributes, ReactNode, useState} from 'react'
import {
    StyledIconRightWrapper,
    StyledInput,
    StyledInputWrapper,
    StyledVisibleToggle
} from '@components/commons/input-text/style.ts'
import {EyeIcon, EyeOffIcon} from 'src/components/ui/icon'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {DefaultNamespace} from 'i18next'
import {Label} from '@components/ui/label/Label.tsx'
import {InputStatusIcon} from '@components/ui/input-status-icon/InputStatusIcon.tsx'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'

export interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
    className?: string
    label?: DefaultNamespace | ReactNode
    name?: string
    type: HTMLInputTypeAttribute
    typeIcon?: ReactNode
    touched?: boolean
    /**
     * how to use errorMessage with translations
     *  errorMessage={t(errors.text?.message || '')} this avoid undefined value problems
     */
    errorMessage?: string | DefaultNamespace
    visibilityToggle?: boolean
    helpText?: string | DefaultNamespace
    placeholder?: string
    inputSize?: 'sm' | 'md' | 'lg'
    width?: CSSProperties['width']
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
    (
        {
            label,
            name,
            type,
            typeIcon,
            touched = false,
            errorMessage,
            helpText,
            className,
            disabled,
            placeholder,
            visibilityToggle = false,
            inputSize = 'md',
            width = '100%',
            ...rest
        },
        ref
    ) => {
        const [eyeVisible, setEyeVisible] = useState(false)

        return (
            <Flexbox className={className} width={width} direction="column" gap={1.5}>
                {label && <Label htmlFor={name}>{label}</Label>}
                <Flexbox align="center" width="100%">
                    <StyledInputWrapper
                        touched={touched}
                        hasError={!!errorMessage}
                        size={inputSize}
                        className={className}
                    >
                        {typeIcon}
                        <StyledInput
                            ref={ref}
                            name={name}
                            type={eyeVisible ? 'text' : type}
                            placeholder={placeholder}
                            disabled={disabled}
                            {...rest}
                        />
                        {/*input Action toggle*/}
                        <StyledIconRightWrapper errorMessage={!!errorMessage} visibilityToggle={visibilityToggle}>
                            {visibilityToggle && (
                                <StyledVisibleToggle
                                    size={inputSize}
                                    disabled={disabled}
                                    onClick={() => setEyeVisible(!eyeVisible)}
                                >
                                    {eyeVisible ? (
                                        <EyeIcon size={20} data-color />
                                    ) : (
                                        <EyeOffIcon size={20} data-color />
                                    )}
                                </StyledVisibleToggle>
                            )}
                            <InputStatusIcon touched={touched} hasError={!!errorMessage} />
                        </StyledIconRightWrapper>
                    </StyledInputWrapper>
                </Flexbox>
                <InputHelpText error={errorMessage} helpText={helpText} />
            </Flexbox>
        )
    }
)

InputText.displayName = 'InputText'
